'use client';

import { useState } from 'react';

import type { AxiosError, AxiosResponse } from 'axios';
import { snakeCase } from 'change-case/keys';

import type { AnyObject } from '@/helpers';
import { cleanObject } from '@/helpers';
import type { RequestOptions } from '@/types/RequestOptions';
import type { ValidationError } from '@/types/ValidationError';

import useAxios from './useAxios';

type UsePostReturn<T, E = unknown> = {
    submit: (url: string, payload: unknown, options: RequestOptions<T, E>) => void;
    response: T | null;
    error: E | null;
    validationErrors: ValidationError[];
    clearErrors: () => void;
    isLoading: boolean;
};

export default function usePost<T, E = unknown>(): UsePostReturn<T, E> {
    // ----------------------------------------------------------------------------------------------------
    // MARK: States & Constants
    // ----------------------------------------------------------------------------------------------------
    const { axiosInstance } = useAxios();
    const [response, setResponse] = useState<T | null>(null);
    const [error, setError] = useState<E | null>(null);
    const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    // ----------------------------------------------------------------------------------------------------
    // MARK: Functions
    // ----------------------------------------------------------------------------------------------------
    function submit(url: string, payload: unknown, options: RequestOptions<T, E> = {}): void {
        // Set default values for constants
        const { stopLoadingOnSuccess = false, stopLoadingOnError = true, onInitialize, onSuccess, onFailed } = options;
        setResponse(null);
        setError(null);
        setValidationErrors([]);
        setIsLoading(true);

        if (onInitialize) {
            onInitialize();
        }
        const cleanPayload = cleanObject(payload as AnyObject);

        axiosInstance
            .post(url, snakeCase(cleanPayload))
            .then((res: AxiosResponse<T>) => {
                setResponse(res.data);
                setError(null);
                setValidationErrors([]);

                if (onSuccess) {
                    onSuccess(res.data);
                }
                if (stopLoadingOnSuccess) {
                    setIsLoading(false);
                }
            })
            .catch((err: AxiosError<E>) => {
                if (onFailed) {
                    onFailed(err.response?.data ?? null, (err.response?.data as { errors?: ValidationError[] }).errors ?? []);
                }

                if (err.response?.status === 422) {
                    setValidationErrors((err.response.data as { errors?: ValidationError[] }).errors ?? []);
                    setError(null);
                } else {
                    setValidationErrors([]);
                    setError(err.response?.data ?? null);
                }
                if (stopLoadingOnError) {
                    setIsLoading(false);
                }
            });
    }

    function clearErrors(): void {
        setValidationErrors([]);
        setError(null);
    }

    // ----------------------------------------------------------------------------------------------------
    // MARK: Return
    // ----------------------------------------------------------------------------------------------------
    return {
        submit,
        response,
        error,
        validationErrors,
        clearErrors,
        isLoading,
    };
}
